import Vue from 'vue';
import axios from "axios";
import Utils from "./utils";
import TokenService from '../../common/token.service';

class AlignmentsStore {

    /**
     * Initialise l'etat reactif du store des alignements.
     */
    constructor() {
        this.state = {
            page: 1,
            itemsPerPage: 10,
            alignmentsList: [],
            extendedSearch: false,
            numberOfAlignments: 10,
            errorInQuery: false,
            searchType: 'classic',
        };
    }

    /**
     * Rafraîchit le token utilisateur s'il est expiré.
     * Utilise le TokenService pour vérifier et renouveler le token d'accès de l'utilisateur.
     * @returns {Promise<void>}
     */
    async refreshUserTokenIfExpired() {
        await TokenService.refreshUserTokenIfExpired(Vue.prototype.$ansAdminAlignments)
    }

    /**
     * Construit l'en-tête d'autorisation Bearer pour les requêtes API sécurisées.
     * @returns {Object} Un objet contenant l'en-tête Authorization avec le token d'accès courant.
     */
    buildAuthorizationHeader() {
        return { 'Authorization': 'Bearer ' + Vue.prototype.$ansAdminAlignments.tokens.accessToken };
    }

    /**
     * Récupère l'URL de base du middleware si disponible.
     * @returns {string|null} URL de base du middleware ou null si absente
     */
    getMiddlewareBaseUrl() {
        return Vue?.prototype?.$searchResultManager?.middlewareBaseUrl || null;
    }

    /**
     * Définit la page actuelle pour la pagination.
     * @param {number} page - Le numéro de page
     */
    setPage(page) {
        this.state.page = page;
    }

    /**
     * Définit le nombre d'éléments par page.
     * @param {number} itemsPerPage - Le nombre d'éléments à afficher par page
     */
    setItemsPerPage(itemsPerPage) {
        this.state.itemsPerPage = itemsPerPage;
    }

    /**
     * Définit le type de recherche.
     * @param {string} searchType - Le type de recherche ('classic' par défaut)
     */
    setSearchType(searchType) {
        this.state.searchType = searchType;
    }

    /**
     * Récupère l'état de la recherche étendue.
     * @returns {boolean} L'état de la recherche étendue
     */
    getExtendedSearch() {
        return this.state.extendedSearch;
    }

    /**
     * Définit l'état de la recherche étendue.
     * @param {boolean} extendedSearch - Active ou désactive la recherche étendue
     */
    setExtendedSearch(extendedSearch) {
        this.state.extendedSearch = extendedSearch;
    }

    /**
     * Remet à zéro les résultats de recherche
     */
    resetSearchResults() {
        this.state.page = 1;
        this.state.errorInQuery = false;
        this.state.alignmentsList = [];
        this.state.numberOfAlignments = 0;
    }

    /**
     * Récupère les données depuis l'API et met à jour l'état du store.
        * Le backend retourne un PublicAlignementListWithCount.
     * @param {string} endpoint - L'endpoint de l'API à appeler
     * @param {Object} params - Les paramètres de la requête
     */
    async getDataFromApi(endpoint, params) {
        // Réinitialiser la page à 1 pour une nouvelle recherche
        this.state.page = 1;
        this.state.errorInQuery = false;
        this.state.alignmentsList = [];
        this.state.numberOfAlignments = 0;

        const middlewareBaseUrl = this.getMiddlewareBaseUrl();
        if (!middlewareBaseUrl) {
            this.state.errorInQuery = true;
            return;
        }

        const axiosParams = Utils.buildCustomParamsForAxios(params);

        await axios.get(
            middlewareBaseUrl + endpoint + '?' + axiosParams
        ).then((response) => {
            this.state.alignmentsList = [...(response?.data?.alignements || [])];
            this.state.numberOfAlignments = response?.data?.numberOfAlignements || 0;
        }).catch(() => {
            this.state.errorInQuery = true;
            this.state.alignmentsList = [];
            this.state.numberOfAlignments = 0;
        });
    }

    /**
     * Charge la page suivante d'alignements et les ajoute à la liste existante.
     * @param {string} endpoint - L'endpoint de l'API à appeler
     * @param {Object} params - Les paramètres de la requête
     */
    async loadMoreAlignments(endpoint, params) {
        this.state.errorInQuery = false;
        const middlewareBaseUrl = this.getMiddlewareBaseUrl();
        if (!middlewareBaseUrl) {
            this.state.errorInQuery = true;
            return;
        }

        const axiosParams = Utils.buildCustomParamsForAxios(params);

        await axios.get(
            middlewareBaseUrl + endpoint + '?' + axiosParams
        ).then((response) => {
            const alignments = response?.data?.alignements || response?.data?.alignments || [];
            const numberOfAlignments = response?.data?.numberOfAlignements || response?.data?.numberOfAlignments || 0;
            this.state.alignmentsList = [
                ...this.state.alignmentsList,
                ...alignments
            ];
            this.state.numberOfAlignments = numberOfAlignments;
        }).catch(() => {
            this.state.errorInQuery = true;
        });
    }

    

}

export default new AlignmentsStore();
